import React, {useEffect, useState} from 'react'
import GuestLoginForm from '../auth/GuestLoginForm'
import GuestLoginCodeForm from '../auth/GuestLoginCodeForm'
import {getUser, setUserToken} from '../../services/token'
import {selectIsAuthenticated} from '../../store/userSlice'
import {setUser} from '../../store/userSlice'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import BrandLogo from '../../assets/placemakr-logo-dusk.png'
import {useLocation, useNavigate} from 'react-router-dom'
import {getLogger} from '../../services/logging'
import {Metric, pushMetric} from '../../services/metrics'
import {trackLoginError} from '../../services/errorAnalytics'
import {
  trackAccountCreated,
  trackFirstLogin
} from '../../services/funnelAnalytics'
import MemberLogin from '../auth/MemberLoginForm'
import CreateMemberAccount from '../account/CreateMemberAccount'
import ResetPasswordCode from '../account/ResetPasswordCode'
import {Button} from 'react-daisyui'
import {ConfirmSignUpCodeForm} from '../account/ConfirmSignUpCodeFirm'
import MemberVerificationSuccess from '../account/MemeberVerificaionSuccess'
import clsx from 'clsx'

const logger = getLogger('LoginPage')

enum LoginMode {
  Member,
  MemberCreateAccount,
  MemberForgotPassword,
  MemberConfirmationCode,
  GuestReservationLookup,
  GuestConfirmationCode,
  VerificationSuccessMessage
}

interface LoginState {
  mode: LoginMode
  hasErrors: boolean
  confirmationCode?: string
  confirming?: boolean
  username?: string
  resetSuccess?: boolean
}

interface LoginLayoutProps {
  header: string
  darkMode?: boolean | null
  loginState: LoginState
  children: any
}

function LoginLayout({
  header,
  darkMode,
  loginState,
  children
}: LoginLayoutProps) {
  const bgColor = darkMode ? 'bg-midnight' : 'bg-white'
  const textColor = darkMode ? 'text-white' : 'text-midnight'
  return (
    <section
      className={`max-w-md min-h-screen mx-auto items-start justify-center md:pt-12`}
      aria-labelledby="login-header"
    >
      <div
        className={`text-center p-10 width-[350px] ${bgColor} min-h-screen md:min-h-fit`}
        role="region"
        aria-live="polite"
      >
        <div className="flex flex-row justify-center mb-4">
          <div
            className="flex flex-row justify-center items-center bg-secondary p-4 w-16 h-16 rounded-full"
            aria-hidden="true"
          >
            <img src={BrandLogo} alt="Placemakr Logo" />
          </div>
        </div>

        {!loginState.hasErrors ? (
          <>
            <h3
              id="login-header"
              className={`max-w-sm text-[1.7rem] font-normal max-w-lg ${textColor}`}
            >
              {header}
            </h3>
          </>
        ) : (
          <>
            <h1 id="login-header" className={`${textColor} text-3xl  `}>
              <span className="font-bold">Oops!</span>
              <br />
              <span className="font-normal">
                Looks like your log in information is incorrect.
              </span>
            </h1>
            <div className={`text-md mt-2 ${textColor}`}>
              Please try logging in again. If you continue to have issues,
              please contact us at
              <a
                className="underline ml-1 cursor-pointer"
                href="mailto:hello@placemakr.com"
                role="button"
                aria-label="Contact us for login issues"
              >
                hello@placemakr.com
              </a>
              .
            </div>
          </>
        )}

        <div className="my-4">{children}</div>
      </div>
    </section>
  )
}

const LoginPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const [loginState, setLoginState] = useState<LoginState>({
    mode: LoginMode.Member,
    confirmationCode: null,
    hasErrors: false
  })

  const path = location.state?.path || '/'

  useEffect(() => {
    if (isAuthenticated) {
      logger.info({
        message: `User is already authenticated, redirecting to ${path}`
      })
      navigate(path)
    }

    if (location.pathname === '/confirm') {
      setLoginState({
        ...loginState,
        mode: LoginMode.MemberCreateAccount,
        confirming: true
      })
    }

    // default to guest login if accessing a guest route
    if (path.startsWith('/guest')) {
      setLoginState({
        ...loginState,
        mode: LoginMode.GuestReservationLookup
      })
    }
  }, [isAuthenticated, path])

  switch (loginState.mode) {
    case LoginMode.Member:
      return (
        <LoginLayout
          header="Placemakr Account"
          darkMode={true}
          loginState={loginState}
        >
          <MemberLogin
            onConfirmSignUp={(username: string) => {
              setLoginState({
                ...loginState,
                mode: LoginMode.MemberConfirmationCode,
                username
              })
            }}
            onLoginSuccess={(token: string) => {
              setUserToken(token)
              const user = getUser(token)
              logger.info({
                message: `Login successful for member.  Redirecting to ${path}...`,
                data: {user, token, path}
              })
              pushMetric(Metric.AuthenticationCompleted, 1)
              dispatch(setUser(user))
              navigate(path)
            }}
            onLoginFailure={() => {
              logger.warn({
                message: `Login unsuccessful for member login`
              })
              pushMetric(Metric.AuthenticationFailed, 1)

              // Track login failure with analytics
              trackLoginError(
                'invalid_credentials',
                'Invalid email or password',
                'email_password'
              )

              setLoginState({
                ...loginState,
                hasErrors: true
              })
            }}
          />
          <Button
            className="dark-mode btn-secondary text-center cursor-pointer mt-4 text-md w-full btn-outline lowercase"
            onClick={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.MemberCreateAccount,
                hasErrors: false
              })
            }}
            aria-label="Create a new account"
          >
            create account
          </Button>

          <div
            className="text-center cursor-pointer mt-4 text-md text-gray-400 lowercase"
            onClick={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.MemberForgotPassword,
                hasErrors: false
              })
            }}
            role="button"
            aria-label="Forgot password"
          >
            Forgot Password
          </div>
        </LoginLayout>
      )
    case LoginMode.VerificationSuccessMessage:
      return (
        <LoginLayout header="Verification Success" loginState={loginState}>
          <MemberVerificationSuccess
            onBackToSignIn={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.Member
              })
            }}
          />
        </LoginLayout>
      )
    case LoginMode.MemberConfirmationCode:
      return (
        <LoginLayout header="Verify Email" loginState={loginState}>
          <CreateMemberAccount
            confirming={true}
            username={loginState.username}
            onLoginSuccess={(token: string) => {
              setUserToken(token)
              const user = getUser(token)
              logger.info({
                message: `Login successful for member.  Redirecting to ${path}...`,
                data: {user, token, path}
              })
              pushMetric(Metric.AuthenticationCompleted, 1)

              // Track first login funnel
              trackFirstLogin(user.email)

              dispatch(setUser(user))
              navigate(path)
            }}
            onLoginFailure={error => {
              logger.warn({
                message: `Login unsuccessful for member login`
              })
              pushMetric(Metric.AuthenticationFailed, 1)

              if ((error as {name?: string})?.name === 'AutoSignInException') {
                // If user has verified but cannot auto sign in, show success message
                // and let them redirect to sign in
                setLoginState({
                  ...loginState,
                  mode: LoginMode.VerificationSuccessMessage
                })
              } else {
                // Track login failure with analytics
                trackLoginError(
                  'invalid_credentials',
                  'Invalid email or password',
                  'email_password'
                )

                setLoginState({
                  ...loginState,
                  hasErrors: true
                })
              }
            }}
          />
        </LoginLayout>
      )
    case LoginMode.MemberCreateAccount:
      return (
        <LoginLayout header="Create Account" loginState={loginState}>
          <CreateMemberAccount
            confirming={loginState.confirming}
            onSignUpSuccess={data => {
              console.log('Sign up success data:', data)

              // Track account creation funnel
              trackAccountCreated(data.email, false)

              setLoginState({
                ...loginState,
                username: data.email,
                mode: LoginMode.MemberConfirmationCode
              })
            }}
            onLoginFailure={() => {
              logger.warn({
                message: `Login unsuccessful for member login`
              })
              pushMetric(Metric.AuthenticationFailed, 1)

              // Track login failure with analytics
              trackLoginError(
                'invalid_credentials',
                'Invalid email or password',
                'email_password'
              )

              setLoginState({
                ...loginState,
                hasErrors: true
              })
            }}
          />
          <div
            className="text-center cursor-pointer mt-4 text-md text-midnight underline"
            onClick={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.Member,
                hasErrors: false
              })
            }}
            role="button"
            aria-label="Back to sign in"
          >
            back to sign in
          </div>
        </LoginLayout>
      )
    case LoginMode.MemberForgotPassword:
      return (
        <LoginLayout header="Forgot Password" loginState={loginState}>
          <ResetPasswordCode
            onResetSuccess={() =>
              setLoginState({
                ...loginState,
                resetSuccess: true
              })
            }
          />
          <div
            className={clsx('', {
              'btn btn-secondary  disabled:text-white justify-center items-center gap-2 px-[60px] mt-6':
                loginState.resetSuccess,
              'text-center cursor-pointer mt-4 text-md underline': !loginState.resetSuccess
            })}
            onClick={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.Member,
                hasErrors: false
              })
            }}
            role="button"
            aria-label="Back to sign in"
          >
            back to sign in
          </div>
          <div
            className="text-[13px] leading-[18px] text-light-midnight
            italic text-center mt-8 -mb-[8px]"
          >
            If you continue to have log in issues, contact us at{' '}
            <a href="mailto:hello@placemakr.com" className="underline">
              hello@placemakr.com
            </a>
          </div>
        </LoginLayout>
      )
    case LoginMode.GuestReservationLookup:
      return (
        <LoginLayout header="Guest Login" loginState={loginState}>
          <GuestLoginForm
            onLoginSuccess={(token: string) => {
              setUserToken(token)
              const user = getUser(token)
              logger.info({
                message: `Login successful for guest using reservation lookup.  Redirecting to ${path}...`,
                data: {token, user, path}
              })
              pushMetric(Metric.AuthenticationCompleted, 1)

              // Track first login funnel
              trackFirstLogin(user.email)

              dispatch(setUser(user))
              navigate(path)
            }}
            onLoginFailure={() => {
              logger.warn({
                message: `Login unsuccessful using reservation lookup`
              })

              // Track login failure with analytics
              trackLoginError(
                'invalid_credentials',
                'Invalid confirmation code',
                'confirmation_code'
              )

              setLoginState({
                ...loginState,
                hasErrors: true
              })
            }}
          />

          <div
            className="text-center cursor-pointer mt-4 text-md text-midnight underline"
            onClick={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.Member,
                hasErrors: false
              })
            }}
            role="button"
            aria-label="Log in as a member"
          >
            I have an account. Log in as a member.
          </div>
          <div
            className="text-center cursor-pointer mt-4 text-md text-midnight underline"
            onClick={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.GuestConfirmationCode
              })
            }}
            role="button"
            aria-label="Log in as a guest with confirmation code"
          >
            I have a confirmation code. Log in as a guest.
          </div>
        </LoginLayout>
      )
    case LoginMode.GuestConfirmationCode:
      return (
        <LoginLayout header="Guest Login" loginState={loginState}>
          <GuestLoginCodeForm
            confirmationCode={loginState.confirmationCode}
            onLoginSuccess={(token: string) => {
              setUserToken(token)
              const user = getUser(token)
              logger.info({
                message: `Login successful for guest using confirmation code.  Redirecting to ${path}...`,
                data: {user, token, path}
              })
              pushMetric(Metric.AuthenticationCompleted, 1)

              // Track first login funnel
              trackFirstLogin(user.email)

              dispatch(setUser(user))
              navigate(path)
            }}
            onLoginFailure={() => {
              logger.warn({
                message: `Login unsuccessful using confirmation code`
              })
              pushMetric(Metric.AuthenticationFailed, 1)

              // Track login failure with analytics
              trackLoginError(
                'invalid_credentials',
                'Invalid confirmation code',
                'confirmation_code'
              )

              setLoginState({
                ...loginState,
                hasErrors: true
              })
            }}
          />
          <div
            className="text-center cursor-pointer mt-4 text-md text-midnight underline"
            onClick={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.Member
              })
            }}
            role="button"
            aria-label="Log in as a member"
          >
            I have an account. Log in as a member.
          </div>
          <div
            className="text-center cursor-pointer mt-4 text-md text-midnight underline"
            onClick={() => {
              setLoginState({
                ...loginState,
                mode: LoginMode.GuestReservationLookup
              })
            }}
            role="button"
            aria-label="Log in as a guest without confirmation code"
          >
            I don't have a confirmation code. Log in as a guest.
          </div>
        </LoginLayout>
      )
    default:
      return <></>
  }
}

export default LoginPage
